// Zombie Crush — Match-3 engine. Pure JS, no React.
//
// Power-tile system (NEW):
//   - Match 4 in a row    -> spawns "stripe-h"   (clears entire row when triggered)
//   - Match 4 in a column -> spawns "stripe-v"   (clears entire column when triggered)
//   - Match 5+ any line   -> spawns "color-bomb" (clears every tile of one color)
//   - Power tiles are stored in a parallel `powers` grid so the underlying
//     tile-type (the "color") remains visible for chaining.
//   - When a power tile is part of a >=3 match it ACTIVATES instead of
//     clearing, which expands the match set and can chain into other powers.
import { ACTIVE_TILES } from "./tiles";

export const ROWS = 7;
export const COLS = 7;
export const TILE_TYPES = ACTIVE_TILES;
const DEFAULT_MOVES = 25;
const LEVEL_GOAL_BASE = 1200;
const LEVEL_GOAL_GROWTH = 1.3;

// Power kinds (string consts so they're greppable across the codebase).
export const POWER = {
  STRIPE_H: "stripe-h",
  STRIPE_V: "stripe-v",
  COLOR_BOMB: "color-bomb",
};

// Human-readable name we toast when a power tile is CREATED.
export const POWER_NAME = {
  [POWER.STRIPE_H]: "Bone Blast!",
  [POWER.STRIPE_V]: "Bone Blast!",
  [POWER.COLOR_BOMB]: "Brain Burst!",
};

// ---- Setup ----------------------------------------------------------------
function rnd() { return Math.floor(Math.random() * TILE_TYPES.length); }

function rndTileExcept(exclude) {
  for (let i = 0; i < 8; i++) {
    const t = TILE_TYPES[rnd()];
    if (!exclude.includes(t)) return t;
  }
  return TILE_TYPES[rnd()];
}

function freshGrid() {
  const g = Array.from({ length: ROWS }, () => Array(COLS).fill(null));
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const exclude = [];
      if (c >= 2 && g[r][c - 1] === g[r][c - 2]) exclude.push(g[r][c - 1]);
      if (r >= 2 && g[r - 1][c] === g[r - 2][c]) exclude.push(g[r - 1][c]);
      g[r][c] = rndTileExcept(exclude);
    }
  }
  return g;
}

function freshIds(state) {
  const ids = Array.from({ length: ROWS }, () => Array(COLS).fill(0));
  for (let r = 0; r < ROWS; r++)
    for (let c = 0; c < COLS; c++) ids[r][c] = state.nextTileId++;
  return ids;
}

function freshPowers() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

export function newGame(level = 1) {
  const state = {
    grid: freshGrid(),
    powers: freshPowers(),
    score: 0,
    moves: DEFAULT_MOVES,
    level,
    goal: Math.round(LEVEL_GOAL_BASE * Math.pow(LEVEL_GOAL_GROWTH, level - 1)),
    tilesCleared: 0,
    nextTileId: 1,
    tileIds: null,
    startTime: Date.now(),
  };
  state.tileIds = freshIds(state);
  // Defensive: if the initial deal somehow has a match, resolve it silently.
  let safety = 5;
  while (findRuns(state.grid).matched.size > 0 && safety-- > 0) {
    state.grid = freshGrid();
    state.tileIds = freshIds(state);
  }
  return state;
}

// ---- Match detection -----------------------------------------------------
// Returns { runs: [{cells:[..], dir:'h'|'v', length, type}], matched: Set }.
// Runs are the raw 3+ sequences before any power-tile activation.
function findRuns(grid) {
  const matched = new Set();
  const runs = [];

  function collect(dir) {
    const outer = dir === "h" ? ROWS : COLS;
    const inner = dir === "h" ? COLS : ROWS;
    for (let a = 0; a < outer; a++) {
      let runStart = 0;
      for (let b = 1; b <= inner; b++) {
        const cur  = b < inner ? (dir === "h" ? grid[a][b] : grid[b][a]) : null;
        const head = (dir === "h" ? grid[a][runStart] : grid[runStart][a]);
        const sameRun = b < inner && cur && cur === head;
        if (!sameRun) {
          const runLen = b - runStart;
          if (runLen >= 3 && head) {
            const cells = [];
            for (let k = runStart; k < b; k++) {
              const key = dir === "h" ? `${a},${k}` : `${k},${a}`;
              matched.add(key);
              cells.push(key);
            }
            runs.push({ cells, dir, length: runLen, type: head });
          }
          runStart = b;
        }
      }
    }
  }
  collect("h");
  collect("v");
  return { runs, matched };
}

export function findMatches(grid) {
  // Back-compat shim — original API returned just the Set.
  return findRuns(grid).matched;
}

// Pick which cell of a run becomes the spawn point of a power tile.
// Prefer a cell that's near the player's last swap, fall back to the middle.
function spawnCellForRun(run, swapHint) {
  if (swapHint) {
    const swapKey = `${swapHint.r},${swapHint.c}`;
    if (run.cells.includes(swapKey)) return swapKey;
  }
  return run.cells[Math.floor(run.cells.length / 2)];
}

// Decide which (if any) power tile a run should spawn.
function runPower(run) {
  if (run.length >= 5) return POWER.COLOR_BOMB;
  if (run.length === 4) return run.dir === "h" ? POWER.STRIPE_H : POWER.STRIPE_V;
  return null;
}

// ---- Power activation expansion ------------------------------------------
// Given a set of currently-matched cells, expand it by activating any power
// tiles within the set. Chains until no new activations.
function expandWithPowers(grid, powers, matched, activatedOut) {
  const queue = Array.from(matched);
  const visited = new Set(queue);
  while (queue.length) {
    const key = queue.shift();
    const [r, c] = key.split(",").map(Number);
    const power = powers[r][c];
    if (!power) continue;
    activatedOut.push({ r, c, power });
    powers[r][c] = null; // consume the power (it just fired)
    let extra = [];
    if (power === POWER.STRIPE_H) {
      for (let cc = 0; cc < COLS; cc++) extra.push(`${r},${cc}`);
    } else if (power === POWER.STRIPE_V) {
      for (let rr = 0; rr < ROWS; rr++) extra.push(`${rr},${c}`);
    } else if (power === POWER.COLOR_BOMB) {
      // Color-bomb when triggered as part of a match clears EVERY tile that
      // shares colors with one of its run-mates. We use the bomb's own type
      // since at spawn time we preserved the underlying tile-type for it.
      const targetType = grid[r][c];
      for (let rr = 0; rr < ROWS; rr++)
        for (let cc = 0; cc < COLS; cc++)
          if (grid[rr][cc] === targetType) extra.push(`${rr},${cc}`);
    }
    for (const k of extra) {
      if (!visited.has(k)) {
        visited.add(k);
        matched.add(k);
        queue.push(k);
      }
    }
  }
}

// ---- Mutators -------------------------------------------------------------
function clone(state) {
  return {
    ...state,
    grid: state.grid.map((row) => row.slice()),
    powers: state.powers.map((row) => row.slice()),
    tileIds: state.tileIds.map((row) => row.slice()),
  };
}

function applyGravityAndRefill(state) {
  for (let c = 0; c < COLS; c++) {
    const stack = [];
    for (let r = ROWS - 1; r >= 0; r--) {
      if (state.grid[r][c] != null) {
        stack.push({
          type: state.grid[r][c],
          id: state.tileIds[r][c],
          power: state.powers[r][c],
        });
      }
    }
    for (let r = ROWS - 1; r >= 0; r--) {
      if (stack.length > 0) {
        const t = stack.shift();
        state.grid[r][c] = t.type;
        state.tileIds[r][c] = t.id;
        state.powers[r][c] = t.power;
      } else {
        state.grid[r][c] = TILE_TYPES[rnd()];
        state.tileIds[r][c] = state.nextTileId++;
        state.powers[r][c] = null;
      }
    }
  }
}

// trySwap — main entry-point. Returns { state, ok, steps, reason }.
// Each step in `steps` is a frame the UI can replay:
//   { matched: [keys], scoreGained, combo, spawned: [{r,c,power}], activated: [{r,c,power}] }
export function trySwap(prev, r1, c1, r2, c2) {
  if (Math.abs(r1 - r2) + Math.abs(c1 - c2) !== 1) {
    return { state: prev, ok: false, reason: "not-adjacent" };
  }
  if (prev.moves <= 0) {
    return { state: prev, ok: false, reason: "no-moves" };
  }
  const state = clone(prev);
  // Perform the swap on all three parallel grids.
  [state.grid[r1][c1],   state.grid[r2][c2]]   = [state.grid[r2][c2],   state.grid[r1][c1]];
  [state.powers[r1][c1], state.powers[r2][c2]] = [state.powers[r2][c2], state.powers[r1][c1]];
  [state.tileIds[r1][c1], state.tileIds[r2][c2]] = [state.tileIds[r2][c2], state.tileIds[r1][c1]];

  // ---- Color-bomb swap shortcut --------------------------------------
  // If either swapped tile is a color-bomb, it activates immediately and
  // clears every tile of the OTHER swap-partner's color — no 3-in-a-row
  // needed. Counts as a valid move.
  const aPow = state.powers[r1][c1];
  const bPow = state.powers[r2][c2];
  if (aPow === POWER.COLOR_BOMB || bPow === POWER.COLOR_BOMB) {
    const bombAt   = aPow === POWER.COLOR_BOMB ? { r: r1, c: c1 } : { r: r2, c: c2 };
    const targetAt = aPow === POWER.COLOR_BOMB ? { r: r2, c: c2 } : { r: r1, c: c1 };
    const targetType = state.grid[targetAt.r][targetAt.c];
    const matched = new Set([`${bombAt.r},${bombAt.c}`]);
    for (let rr = 0; rr < ROWS; rr++)
      for (let cc = 0; cc < COLS; cc++)
        if (state.grid[rr][cc] === targetType) matched.add(`${rr},${cc}`);
    state.powers[bombAt.r][bombAt.c] = null;
    const activated = [{ r: bombAt.r, c: bombAt.c, power: POWER.COLOR_BOMB }];
    expandWithPowers(state.grid, state.powers, matched, activated);
    return runCascade(state, matched, activated, [], { r: r2, c: c2 });
  }

  const initial = findRuns(state.grid);
  if (initial.matched.size === 0) {
    return { state: prev, ok: false, reason: "no-match" };
  }
  return runCascade(state, initial.matched, [], initial.runs, { r: r2, c: c2 });
}

// Shared cascade loop used both for normal swaps and color-bomb swaps.
function runCascade(state, matchedFirst, activatedFirst, runsFirst, swapHint) {
  const steps = [];
  let combo = 0;
  let matched = matchedFirst;
  let runs = runsFirst;
  let activated = activatedFirst;

  while (matched.size > 0) {
    combo++;
    // Expand the matched set by any power tiles caught in it.
    expandWithPowers(state.grid, state.powers, matched, activated);

    // Decide which cells should be CONVERTED into power tiles instead of
    // cleared — one per qualifying run, only on the first cascade frame
    // (subsequent cascades just clear; spawning new powers from cascade
    // chains keeps things spicy but balanced for level pacing).
    const spawns = [];
    const protectedCells = new Set();
    if (combo === 1) {
      for (const run of runs) {
        const power = runPower(run);
        if (!power) continue;
        const cellKey = spawnCellForRun(run, swapHint);
        if (protectedCells.has(cellKey)) continue;
        protectedCells.add(cellKey);
        const [pr, pc] = cellKey.split(",").map(Number);
        spawns.push({ r: pr, c: pc, power, type: state.grid[pr][pc] });
      }
    }

    // Score: base + cascade bonus + power-spawn bonus.
    const base = matched.size * 10;
    const comboBonus = combo > 1 ? Math.round(base * 0.5 * (combo - 1)) : 0;
    const powerBonus = spawns.length * 30 + activated.length * 50;
    const scoreGained = base + comboBonus + powerBonus;
    state.score += scoreGained;
    state.tilesCleared += matched.size;

    steps.push({
      matched: Array.from(matched),
      scoreGained,
      combo,
      spawned: spawns.map((s) => ({ r: s.r, c: s.c, power: s.power })),
      activated: activated.slice(),
    });

    // Clear matched cells EXCEPT the ones promoted to power tiles.
    for (const key of matched) {
      const [r, c] = key.split(",").map(Number);
      if (protectedCells.has(key)) {
        // Convert to power tile, keep underlying type.
        const spawn = spawns.find((s) => s.r === r && s.c === c);
        state.powers[r][c] = spawn.power;
        // Tile-id stays so the UI can animate the same DOM element.
        continue;
      }
      state.grid[r][c] = null;
      state.tileIds[r][c] = 0;
      state.powers[r][c] = null;
    }

    applyGravityAndRefill(state);

    // Look for new cascade matches.
    const next = findRuns(state.grid);
    matched = next.matched;
    runs = next.runs;
    activated = [];
  }

  state.moves -= 1;
  return { state, ok: true, steps };
}

// Detect if there's at least one move available — also returns true if any
// color-bomb exists on the board (since a color-bomb can always be swapped).
export function hasAnyMove(grid, powers) {
  if (powers) {
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++)
        if (powers[r][c] === POWER.COLOR_BOMB) return true;
  }
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      for (const [dr, dc] of [[0, 1], [1, 0]]) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr >= ROWS || nc >= COLS) continue;
        const trial = grid.map((row) => row.slice());
        [trial[r][c], trial[nr][nc]] = [trial[nr][nc], trial[r][c]];
        if (findMatches(trial).size > 0) return true;
      }
    }
  }
  return false;
}

export function shuffleBoard(state) {
  const next = clone(state);
  next.grid = freshGrid();
  next.powers = freshPowers();
  next.tileIds = freshIds(next);
  return next;
}
